import DOMPurify from "dompurify";

// ============================================================================
// YAML syntax highlighting
// ============================================================================

const highlightYamlLine = (line: string): string => {
    if (!line.trim()) return line;

    // Full-line comments
    if (/^\s*#/.test(line)) {
        return `<span class="c">${line}</span>`;
    }

    let result = line;

    // Quoted strings "..."
    result = result.replace(/"([^"]*?)"/g, '<span class="s">"$1"</span>');

    // YAML keys before colon
    result = result.replace(
        /^(\s*(?:[-*]\s+)?)([A-Za-z_][\w.-]*)(:\s*)/,
        '$1<span class="k">$2</span>$3'
    );

    return result;
};

const addYamlSyntaxHighlighting = (html: string): string => {
    return html.replace(
        /<pre><code(?:\s[^>]*)?>([\s\S]*?)<\/code><\/pre>/gi,
        (_match, codeContent: string) => {
            const normalized = codeContent.replace(/<br\s*\/?>/gi, "\n");

            const highlighted = normalized
                .split("\n")
                .map((line) => highlightYamlLine(line))
                .join("<br>");

            return `<pre><code class="language-yaml">${highlighted}</code></pre>`;
        }
    );
};

// ============================================================================
// Pipe table → <table> conversion
// ============================================================================

const parsePipeRow = (line: string): string[] => {
    const trimmed = line.replace(/^\|/, "").replace(/\|$/, "");
    return trimmed.split("|").map((cell) => cell.trim());
};

const convertPipeTablesToHtml = (html: string): string => {
    return html.replace(
        /<p>([\s\S]*?)<\/p>/g,
        (match, innerContent: string) => {
            const lines = innerContent
                .split(/<br\s*\/?>|\n/)
                .map((l) => l.trim())
                .filter((l) => l.length > 0);

            if (lines.length < 3) return match;

            const pipeLines = lines.filter((l) => l.includes("|"));
            if (pipeLines.length < lines.length * 0.8) return match;

            const separatorIndex = lines.findIndex(
                (line) =>
                    line.includes("|") &&
                    line.includes("-") &&
                    line.replace(/[|\s:-]/g, "").length === 0
            );

            if (separatorIndex < 1) return match;

            const headerLines = lines.slice(0, separatorIndex);
            const bodyLines = lines
                .slice(separatorIndex + 1)
                .filter((line) => line.includes("|"));

            if (bodyLines.length === 0) return match;

            let table = "<table><thead>";

            headerLines.forEach((line) => {
                table += "<tr>";
                parsePipeRow(line).forEach((cell) => {
                    table += `<th>${cell}</th>`;
                });
                table += "</tr>";
            });

            table += "</thead><tbody>";

            bodyLines.forEach((line) => {
                table += "<tr>";
                parsePipeRow(line).forEach((cell) => {
                    table += `<td>${cell}</td>`;
                });
                table += "</tr>";
            });

            table += "</tbody></table>";

            return table;
        }
    );
};

// ============================================================================
// Asterisk list fix
// ============================================================================

const fixAsterisksAsListItems = (html: string): string => {
    return html.replace(
        /<pre><code(?:\s[^>]*)?>([\s\S]*?)<\/code><\/pre>/gi,
        (match, codeContent: string) => {
            const fixed = codeContent.replace(/^(\s*)\* /gm, "$1- ");
            return match.replace(codeContent, fixed);
        }
    );
};

// ============================================================================
// Basic markdown → HTML fallback
// ============================================================================

const convertBasicMarkdown = (md: string): string => {
    let html = md;

    html = html
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    // Code blocks
    html = html.replace(
        /```(\w*)\n([\s\S]*?)```/g,
        (_match, _lang, code) => `<pre><code>${code.trim()}</code></pre>`
    );

    // Inline code
    html = html.replace(/`([^`]+)`/g, "<code>$1</code>");

    // Headings
    html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
    html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
    html = html.replace(/^# (.+)$/gm, "<h1>$1</h1>");

    // Bold
    html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");

    // Italic
    html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");

    // Links
    html = html.replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );

    // Unordered lists
    html = html.replace(/^[\-\*] (.+)$/gm, "<li>$1</li>");
    html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, "<ul>$1</ul>");

    // Paragraphs
    html = html
        .split("\n\n")
        .map((block) => {
            const trimmed = block.trim();
            if (!trimmed) return "";
            if (/^<[a-z]/.test(trimmed)) return trimmed;
            return `<p>${trimmed.replace(/\n/g, "<br>")}</p>`;
        })
        .join("\n");

    return html;
};

// ============================================================================
// Post-processing pipeline
// ============================================================================

const postProcessReadmeHtml = (html: string): string => {
    let processed = html;
    processed = fixAsterisksAsListItems(processed);
    processed = convertPipeTablesToHtml(processed);
    processed = addYamlSyntaxHighlighting(processed);
    return processed;
};

// ============================================================================
// Public API
// ============================================================================

/**
 * Fetch the README for a Bitbucket pipe repository and return
 * sanitized, post-processed HTML ready for rendering.
 *
 * - Tries README.md first, then pipe.md as fallback
 * - Converts raw markdown if Bitbucket returns plain text
 * - Adds YAML syntax highlighting to code blocks
 * - Converts pipe-delimited tables to proper <table> HTML
 * - Sanitizes via DOMPurify
 */
export const fetchReadmeHtml = async (
    repositoryPath: string
): Promise<string | null> => {
    try {
        // Try README.md first
        const rawUrl = `https://api.bitbucket.org/2.0/repositories/${repositoryPath}/src/master/README.md`;

        const res = await fetch(rawUrl, {
            headers: { Accept: "text/html" }
        });

        if (res.ok) {
            const text = await res.text();
            const html = text.trim().startsWith("<")
                ? text
                : convertBasicMarkdown(text);
            return DOMPurify.sanitize(postProcessReadmeHtml(html));
        }

        // Fallback: pipe.md
        const pipeUrl = `https://api.bitbucket.org/2.0/repositories/${repositoryPath}/src/master/pipe.md`;
        const pipeRes = await fetch(pipeUrl, {
            headers: { Accept: "text/html" }
        });

        if (pipeRes.ok) {
            const text = await pipeRes.text();
            const html = text.trim().startsWith("<")
                ? text
                : convertBasicMarkdown(text);
            return DOMPurify.sanitize(postProcessReadmeHtml(html));
        }

        return null;
    } catch {
        return null;
    }
};

/**
 * Scoped CSS for Pygments-style syntax highlighting classes.
 * Inject via <style> inside the README container.
 */
export const SYNTAX_STYLES = `
  .bb-readme-content .k,
  .bb-readme-content .kn,
  .bb-readme-content .kd,
  .bb-readme-content .kp,
  .bb-readme-content .kt {
    color: #0052CC;
    font-weight: 600;
  }

  .bb-readme-content .s,
  .bb-readme-content .s1,
  .bb-readme-content .s2,
  .bb-readme-content .se,
  .bb-readme-content .sb,
  .bb-readme-content .sc,
  .bb-readme-content .sd,
  .bb-readme-content .sh,
  .bb-readme-content .si,
  .bb-readme-content .sx,
  .bb-readme-content .sr,
  .bb-readme-content .ss {
    color: #36B37E;
  }

  .bb-readme-content .c,
  .bb-readme-content .c1,
  .bb-readme-content .cm,
  .bb-readme-content .cp,
  .bb-readme-content .cs,
  .bb-readme-content .ch {
    color: #6B778C;
    font-style: italic;
  }

  .bb-readme-content .nf,
  .bb-readme-content .nb,
  .bb-readme-content .nc,
  .bb-readme-content .ni,
  .bb-readme-content .ne,
  .bb-readme-content .nd {
    color: #6554C0;
  }

  .bb-readme-content .mi,
  .bb-readme-content .mf,
  .bb-readme-content .mh,
  .bb-readme-content .mo,
  .bb-readme-content .mb,
  .bb-readme-content .il {
    color: #FF5630;
  }

  .bb-readme-content .o,
  .bb-readme-content .p,
  .bb-readme-content .ow {
    color: #091E42;
  }

  .bb-readme-content .n,
  .bb-readme-content .na,
  .bb-readme-content .no,
  .bb-readme-content .nv,
  .bb-readme-content .nn,
  .bb-readme-content .nt,
  .bb-readme-content .nx,
  .bb-readme-content .bp,
  .bb-readme-content .vc,
  .bb-readme-content .vg,
  .bb-readme-content .vi,
  .bb-readme-content .vm {
    color: #091E42;
  }
`;

/** Class name to scope the syntax styles */
export const README_SCOPE_CLASS = "bb-readme-content";
